/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */


import { Component, Input, ElementRef, Renderer, ViewEncapsulation } from '@angular/core';
import { SimpleChange, SimpleChanges, OnChanges, OnDestroy } from '@angular/core';
import classNames from 'classnames';
export class Button implements OnChanges, OnDestroy {
/**
 * 样式前缀
 */
prefixCls = 'ant-btn';
/**
 * 设置按钮类型，可选值为 `primary` `dashed` `danger`，或者不设 <br>
 * 按钮类型，取值：default、primary、warn等。默认为default。<br>
 * 自定义的颜色名称与色值，可以定义在 工程根目录/src/theme/variables.scss 文件中的 $colors 对象。
 */
type: string;
/**
 * 设置 `button` 原生的 `type` 值，可选值请参考 [HTML 标准](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#attr-type) <br>
 * 可选值为`submit`, `button`, `reset`，默认取值为`button`。
 */
htmlType = 'button';
/**
 * 设置按钮的图标类型。例如：search、download等。<br>
 * 当需要在 <code>Button</code> 内嵌入 <code>Icon</code> 时，可以设置 <code>icon</code> 属性，或者直接在 <code>Button</code> 内使用 <code>Icon</code> 组件。<br>
 * 如果想控制 <code>Icon</code> 具体的位置，只能直接使用 <code>Icon</code> 组件，而非 <code>icon</code> 属性。
 */
icon: string;
/**
 * 设置按钮形状，可选值为 `circle` 或者不设。可选值为`circle`, `circle-outline'`。
 */
shape: string;
/**
 * 设置按钮大小。可选值为`large`, `small`, `default`，默认取值为`default`。
 */
size: string;
/**
 * 设置按钮载入状态。默认为false。
 */
loading: boolean | { delay?: number } = false;
/**
 * 禁用样式
 */
disabled = false;
/**
 * 幽灵属性，使按钮背景透明
 */
ghost = false;
/**
 * 用户CSS样式
 */
class: string;
private clicked = false;
private timeout: any;
private delayTimeout: any;
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 */
constructor(private _renderer: Renderer,
private _elementRef: ElementRef) {

    }
/**
 * 最终样式
 * @return {?}
 */
get classes(): string {
        const /** @type {?} */ hasText = this._elementRef.nativeElement.querySelector('span').hasChildNodes();
        const /** @type {?} */ classes = classNames(this.prefixCls, {
            [`${this.prefixCls}-${this.type}`]: this.type,
            [`${this.prefixCls}-${this.shape}`]: this.shape,
            [`${this.prefixCls}-${this.sizeCls}`]: this.sizeCls,
            [`${this.prefixCls}-icon-only`]: this.icon && !hasText,
            [`${this.prefixCls}-loading`]: this.loading,
            [`${this.prefixCls}-clicked`]: this.clicked,
            [`${this.prefixCls}-background-ghost`]: this.ghost,
        }, this.class);
        return classes;
    }
/**
 * @return {?}
 */
get sizeCls(): string {
        // large => lg
        // small => sm
        return ( /** @type {?} */((<any>{
            'large': 'lg',
            'small': 'sm'
        })))[this.size] || '';
    }
/**
 * @return {?}
 */
get iconType(): string {
        return this.loading ? 'loading' : this.icon;
    }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void {
        const /** @type {?} */ loadingChanged: SimpleChange = changes['loading'];
        if (loadingChanged) {
            if (this.loading) {
                clearTimeout(this.delayTimeout);
            }

            const /** @type {?} */ loading: any = loadingChanged.currentValue;
            if (typeof loading !== 'boolean' && loading && loading.delay) {
                loadingChanged.currentValue = loadingChanged.previousValue; // hold the trigger of changing here
                this.delayTimeout = setTimeout(() => { this.loading = loading; }, loading.delay);
            } else {
                this.loading = loading;
            }
        }
    }
/**
 * @return {?}
 */
ngOnDestroy(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        if (this.delayTimeout) {
            clearTimeout(this.delayTimeout);
        }
    }
/**
 * @param {?} e
 * @return {?}
 */
handleClick(e: MouseEvent): void {
        // Add click effect
        this.clicked = true;
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
        this.timeout = setTimeout(() => this.clicked = false, 500);
    }
/**
 * @param {?} e
 * @return {?}
 */
handleMouseUp(e: MouseEvent) {
        this._elementRef.nativeElement.querySelector('button').blur();
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'ant-button',
    template: `
        <button
        [class]="classes"
        [disabled]="disabled"
        [type]="htmlType"
        (click)="handleClick($event)"
        (mouseup)="handleMouseUp($event)"
        >
            <ant-icon [type]="iconType" *ngIf="iconType"></ant-icon>
            <span><ng-content></ng-content></span>
        </button>
    `,
    styleUrls: ['./style/index.scss'],
    encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'type': [{ type: Input },],
'htmlType': [{ type: Input },],
'icon': [{ type: Input },],
'shape': [{ type: Input },],
'size': [{ type: Input },],
'loading': [{ type: Input },],
'disabled': [{ type: Input },],
'ghost': [{ type: Input },],
'class': [{ type: Input },],
};
}

function Button_tsickle_Closure_declarations() {
/** @type {?} */
Button.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Button.ctorParameters;
/** @type {?} */
Button.propDecorators;
/**
 * 样式前缀
 * @type {?}
 */
Button.prototype.prefixCls;
/**
 * 设置按钮类型，可选值为 `primary` `dashed` `danger`，或者不设 <br>
 * 按钮类型，取值：default、primary、warn等。默认为default。<br>
 * 自定义的颜色名称与色值，可以定义在 工程根目录/src/theme/variables.scss 文件中的 $colors 对象。
 * @type {?}
 */
Button.prototype.type;
/**
 * 设置 `button` 原生的 `type` 值，可选值请参考 [HTML 标准](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#attr-type) <br>
 * 可选值为`submit`, `button`, `reset`，默认取值为`button`。
 * @type {?}
 */
Button.prototype.htmlType;
/**
 * 设置按钮的图标类型。例如：search、download等。<br>
 * 当需要在 <code>Button</code> 内嵌入 <code>Icon</code> 时，可以设置 <code>icon</code> 属性，或者直接在 <code>Button</code> 内使用 <code>Icon</code> 组件。<br>
 * 如果想控制 <code>Icon</code> 具体的位置，只能直接使用 <code>Icon</code> 组件，而非 <code>icon</code> 属性。
 * @type {?}
 */
Button.prototype.icon;
/**
 * 设置按钮形状，可选值为 `circle` 或者不设。可选值为`circle`, `circle-outline'`。
 * @type {?}
 */
Button.prototype.shape;
/**
 * 设置按钮大小。可选值为`large`, `small`, `default`，默认取值为`default`。
 * @type {?}
 */
Button.prototype.size;
/**
 * 设置按钮载入状态。默认为false。
 * @type {?}
 */
Button.prototype.loading;
/**
 * 禁用样式
 * @type {?}
 */
Button.prototype.disabled;
/**
 * 幽灵属性，使按钮背景透明
 * @type {?}
 */
Button.prototype.ghost;
/**
 * 用户CSS样式
 * @type {?}
 */
Button.prototype.class;
/** @type {?} */
Button.prototype.clicked;
/** @type {?} */
Button.prototype.timeout;
/** @type {?} */
Button.prototype.delayTimeout;
/** @type {?} */
Button.prototype._renderer;
/** @type {?} */
Button.prototype._elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
